import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAppStore } from '../../contexts/store';
import { enabledModuleSet, moduleForPermission, MODULE_LABELS, type ProductModule } from '../../utils/productModules';
import { ThemeToggle } from '../common/ThemeToggle';
import { CommandPalette } from '../assistant/CommandPalette';
import { QuickSearch } from '../common/QuickSearch';
import { useDoubleShift } from '../../hooks';
import {
  LayoutDashboard,
  MapPin,
  Users,
  Calendar,
  Building2,
  BarChart3,
  FileText,
  FileSpreadsheet,
  FileCheck,
  Settings,
  LogOut,
  Menu,
  X,
  Layers,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Wrench,
  PieChart,
  GitBranch,
  UserCheck,
  Router,
  Radio,
  Package,
  Ticket,
  DollarSign,
  Mail,
  AlertTriangle,
  Send,
  ClipboardList,
  BookOpen,
  HelpCircle,
  CreditCard,
  Receipt,
  Percent,
  Gift,
  Play,
  Clock,
  User,
  Cable,
  Network,
  Sparkles,
  QrCode,
  FileStack,
  Phone,
  MessageCircle,
  MessageSquare,
  ListChecks,
  Search,
  Warehouse,
  Shield,
  ArrowLeftRight,
  Truck,
  Boxes,
  Megaphone,
  RefreshCw,
  Workflow,
  Tag,
  Compass,
  Flag,
  Lock,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ size?: number }>;
  permission?: string; // Optional permission required to see this item
  /** Product module this item belongs to; derived from `permission` when omitted */
  module?: ProductModule;
  /** Set by the filter: the tenant's plan lacks the module, so the item is greyed and links to the upgrade page */
  locked?: boolean;
  integrationKey?: 'adtran' | 'calix' | 'uisp' | 'radius' | 'netBox' | 'libreNms' | 'oneCall'; // Optional: only show if integration is enabled
  integrationKeys?: ('adtran' | 'calix' | 'cambium' | 'uisp')[]; // Optional: show if ANY of these integrations are enabled
}

interface NavGroup {
  name: string;
  icon: React.ComponentType<{ size?: number }>;
  items: NavItem[];
  permission?: string; // Optional permission required to see entire group
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

export function DashboardLayout() {
  const { tenantConfig, user, logout } = useAppStore();
  const { hasPermission, permissions } = usePermissions();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);

  // Double-shift detection for Quick Search
  useDoubleShift(() => {
    setIsQuickSearchOpen(true);
  });

  // Load collapsed state from localStorage, keyed by user to reset on login
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const savedKey = localStorage.getItem('nav-collapsed-groups-user');
    const saved = localStorage.getItem('nav-collapsed-groups');
    // Reset collapsed state if user changed (e.g., new login)
    if (savedKey !== user?.id) {
      localStorage.removeItem('nav-collapsed-groups');
      return new Set();
    }
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Reset collapsed state when user changes (e.g., after login)
  useEffect(() => {
    if (user?.id) {
      const savedKey = localStorage.getItem('nav-collapsed-groups-user');
      if (savedKey !== user.id) {
        // New user, reset collapsed groups
        localStorage.removeItem('nav-collapsed-groups');
        localStorage.setItem('nav-collapsed-groups-user', user.id);
        setCollapsedGroups(new Set());
      }
    }
  }, [user?.id]);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('nav-collapsed-groups', JSON.stringify([...collapsedGroups]));
  }, [collapsedGroups]);

  // Command palette keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Check if we're on superadmin routes
  const isSuperAdminRoute = location.pathname.startsWith('/superadmin');
  const isSuperAdmin = user?.role === 'SuperAdmin';

  // SuperAdmin navigation (shown on /superadmin routes) - memoized to prevent infinite re-renders
  const superAdminNavigation: NavEntry[] = useMemo(() => [
    { name: 'Tenants', href: '/superadmin/tenants', icon: Building2 },
    { name: 'Analytics', href: '/superadmin/analytics', icon: BarChart3 },
    { name: 'One-Call Patterns', href: '/superadmin/onecall-patterns', icon: Phone },
    { name: 'Outreach Email', href: '/superadmin/outreach', icon: Mail },
    { name: 'SMS Registrations', href: '/superadmin/sms-registrations', icon: MessageSquare },
  ], []);

  const navigation: NavEntry[] = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Operations Center', href: '/dashboard/operations', icon: Compass, permission: 'dispatch.view' },
    {
      name: 'Customers',
      icon: UserCheck,
      items: [
        { name: 'Accounts', href: '/dashboard/accounts', icon: UserCheck, permission: 'accounts.view' },
        { name: 'Leads', href: '/dashboard/leads', icon: Users, permission: 'leads.view' },
        { name: 'Qualification Lists', href: '/dashboard/qualification-lists', icon: ListChecks, permission: 'qualification-lists.view' },
        { name: 'Canvass', href: '/dashboard/canvass', icon: Flag, permission: 'canvass.view' },
        { name: 'Addresses', href: '/dashboard/addresses', icon: Building2, permission: 'addresses.view' },
        { name: 'Tickets', href: '/dashboard/tickets', icon: Ticket, permission: 'tickets.view' },
        { name: 'Messages', href: '/dashboard/messages', icon: MessageSquare, permission: 'sms.view' },
      ],
    },
    {
      name: 'Marketing',
      icon: Megaphone,
      items: [
        { name: 'Campaigns', href: '/dashboard/campaigns', icon: Send, permission: 'campaigns.view' },
        { name: 'Win-Back', href: '/dashboard/winback-campaigns', icon: RefreshCw, permission: 'winback.view' },
        { name: 'Nurturing', href: '/dashboard/nurturing', icon: Workflow, permission: 'nurturing.view' },
        { name: 'Promo Codes', href: '/dashboard/promo-codes', icon: Tag, permission: 'promo-codes.view' },
        { name: 'Referrals', href: '/dashboard/referrals', icon: Gift, permission: 'settings.view' },
      ],
    },
    {
      name: 'Operations',
      icon: Wrench,
      items: [
        { name: 'Scheduling', href: '/dashboard/scheduling', icon: Calendar, permission: 'scheduling.view' },
        { name: 'Dispatch Board', href: '/dashboard/dispatch', icon: ClipboardList, permission: 'dispatch.view' },
        { name: 'Contractors', href: '/dashboard/contractors', icon: Users, permission: 'contractors.view' },
        { name: 'Pending Provisioning', href: '/dashboard/pending-provisioning', icon: Package, permission: 'installations.view' },
        { name: 'Workflow Queue', href: '/dashboard/workflow-queue', icon: GitBranch, permission: 'installations.view' },
        { name: 'Chat Approvals', href: '/dashboard/chat-approvals', icon: MessageCircle, permission: 'chat.approvals.view' },
        { name: 'Locate Tickets', href: '/dashboard/locate-tickets', icon: Phone, permission: 'locate_tickets.view' },
        { name: 'Outages', href: '/dashboard/outages', icon: AlertTriangle, permission: 'outages.view' },
      ],
    },
    {
      name: 'Network',
      icon: Network,
      items: [
        { name: 'Service Areas', href: '/dashboard/service-areas', icon: MapPin, permission: 'service_areas.view' },
        { name: 'Fiber Mapping', href: '/dashboard/fiber-mapping', icon: Cable, permission: 'fiber.view' },
        { name: 'Parcel Layers', href: '/dashboard/parcel-layers', icon: Layers, permission: 'parcels.view' },
        { name: 'Permits', href: '/dashboard/permits', icon: FileStack, permission: 'permits.view' },
        { name: 'Network Sites', href: '/dashboard/network-sites', icon: Radio, permission: 'network-sites.view' },
        { name: 'Topology', href: '/dashboard/network-topology', icon: Network, permission: 'network-sites.view' },
        { name: 'IP Addresses', href: '/dashboard/ip-addresses', icon: Network, permission: 'network-sites.view' },
        { name: 'NMS', href: '/dashboard/nms', icon: Radio, integrationKeys: ['adtran', 'calix', 'cambium', 'uisp'] },
      ],
    },
    {
      name: 'Services',
      icon: Layers,
      items: [
        { name: 'Service Tiers', href: '/dashboard/service-tiers', icon: Layers, permission: 'services.view' },
        { name: 'Service Bundles', href: '/dashboard/service-bundles', icon: Package, permission: 'services.view' },
        { name: 'Site Plans', href: '/dashboard/site-plans', icon: FileText, permission: 'site-plans.view' },
        { name: 'Site Templates', href: '/dashboard/site-templates', icon: ClipboardList, permission: 'services.view' },
        { name: 'Add-Ons', href: '/dashboard/settings/add-ons', icon: DollarSign, permission: 'services.view' },
      ],
    },
    {
      name: 'Billing',
      icon: DollarSign,
      permission: 'billing.view',
      items: [
        { name: 'Overview', href: '/dashboard/billing', icon: DollarSign },
        { name: 'Invoices', href: '/dashboard/invoices', icon: Receipt },
        { name: 'Paper Invoices', href: '/dashboard/paper-invoices', icon: Mail },
        { name: 'Payments', href: '/dashboard/payments', icon: CreditCard },
        { name: 'Bill Runs', href: '/dashboard/bill-runs', icon: Play, permission: 'billing.edit' },
        { name: 'Credits', href: '/dashboard/credits', icon: Gift },
        { name: 'Tax Rates', href: '/dashboard/tax-rates', icon: Percent, permission: 'billing.edit' },
      ],
    },
    {
      name: 'Inventory',
      icon: Package,
      items: [
        { name: 'Equipment', href: '/dashboard/equipment', icon: Router, permission: 'equipment.view' },
        { name: 'QR Codes', href: '/dashboard/equipment/qr-codes', icon: QrCode, permission: 'equipment.view' },
        { name: 'Materials', href: '/dashboard/materials', icon: Package, permission: 'materials.view' },
        { name: 'Locations', href: '/dashboard/settings/locations', icon: Warehouse, permission: 'settings.edit' },
        { name: 'Stock Levels', href: '/dashboard/inventory/stock', icon: Boxes, permission: 'equipment.view' },
        { name: 'Transfer', href: '/dashboard/inventory/transfer', icon: ArrowLeftRight, permission: 'equipment.manage' },
      ],
    },
    {
      name: 'Fleet',
      icon: Truck,
      permission: 'fleet.view',
      items: [
        { name: 'Vehicles', href: '/dashboard/fleet', icon: Truck, permission: 'fleet.view' },
        { name: 'Issues', href: '/dashboard/fleet/issues', icon: AlertTriangle, permission: 'fleet.view' },
        { name: 'Reports', href: '/dashboard/fleet/reports', icon: BarChart3, permission: 'fleet.view' },
        { name: 'Vehicle Types', href: '/dashboard/settings?tab=fleet', icon: Wrench, permission: 'fleet.manage' },
      ],
    },
    {
      name: 'Reports',
      icon: PieChart,
      permission: 'reports.view',
      items: [
        { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
        { name: 'Feedback', href: '/dashboard/analytics/feedback', icon: BarChart3 },
        { name: 'Reports', href: '/dashboard/reports', icon: FileText },
        { name: 'AR Aging', href: '/dashboard/reports/ar-aging', icon: Clock },
        { name: 'Payment Summary', href: '/dashboard/reports/payment-summary', icon: DollarSign },
        { name: 'Operational', href: '/dashboard/operational-reports', icon: FileSpreadsheet },
        { name: 'Compliance', href: '/dashboard/compliance', icon: FileCheck, permission: 'compliance.view' },
      ],
    },
    {
      name: 'Settings',
      icon: Settings,
      permission: 'settings.view',
      items: [
        { name: 'Settings', href: '/dashboard/settings', icon: Settings },
        { name: 'Survey Templates', href: '/dashboard/survey-templates', icon: ClipboardList },
        { name: 'Departments', href: '/dashboard/departments', icon: Building2 },
        { name: 'Email Templates', href: '/dashboard/email-templates', icon: Mail },
        { name: 'SMS Templates', href: '/dashboard/sms-templates', icon: MessageCircle, permission: 'sms.view' },
        { name: 'SMS History', href: '/dashboard/sms-history', icon: Phone, permission: 'sms.view' },
        { name: 'Onboarding Documents', href: '/dashboard/settings/onboarding-documents', icon: FileCheck },
        {
          name: 'Address-Check Protection',
          href: '/dashboard/settings/serviceability-protection',
          icon: Shield,
          permission: 'serviceability_protection.view',
        },
        { name: 'User Guide', href: '/dashboard/help', icon: BookOpen },
        { name: 'FAQ & Help', href: '/dashboard/faq', icon: HelpCircle },
      ],
    },
  ];

  // Helper to check if an integration is enabled
  const isIntegrationEnabled = (key?: NavItem['integrationKey'], keys?: NavItem['integrationKeys']): boolean => {
    // If integrationKeys array is provided, show if ANY are enabled
    if (keys && keys.length > 0) {
      return keys.some(k => tenantConfig?.enabledIntegrations?.[k] === true);
    }
    if (!key) return true; // No integration key means always show
    return tenantConfig?.enabledIntegrations?.[key] === true;
  };

  // Modules the tenant's plan includes. Items from other modules stay visible
  // but greyed, linking to the upgrade page, so people see what exists.
  const enabledModules = useMemo(
    () => enabledModuleSet(tenantConfig?.enabledModules),
    [tenantConfig?.enabledModules]
  );
  const withModuleLock = (item: NavItem): NavItem => {
    const module = item.module ?? moduleForPermission(item.permission) ?? undefined;
    if (!module || enabledModules.has(module)) return item;
    return { ...item, locked: true, module, href: `/dashboard/upgrade?module=${module}` };
  };

  // Filter navigation based on user permissions and enabled integrations
  const filteredNavigation = useMemo(() => {
    // Use superadmin navigation when on superadmin routes
    if (isSuperAdminRoute && isSuperAdmin) {
      return superAdminNavigation;
    }

    return navigation
      .map(entry => {
        // For groups, filter items and check group permission
        if (isNavGroup(entry)) {
          // If group has a permission requirement, check it first
          if (entry.permission && !hasPermission(entry.permission)) {
            return null;
          }
          // Filter items within the group (check both permission and integration),
          // then mark items whose module the plan lacks as locked
          const filteredItems = entry.items
            .filter(item =>
              (!item.permission || hasPermission(item.permission)) &&
              isIntegrationEnabled(item.integrationKey, item.integrationKeys)
            )
            .map(withModuleLock);
          // Return null if no items remain after filtering
          if (filteredItems.length === 0) {
            return null;
          }
          return { ...entry, items: filteredItems };
        }
        // For single items, check permission and integration
        if (entry.permission && !hasPermission(entry.permission)) {
          return null;
        }
        return withModuleLock(entry);
      })
      .filter((entry): entry is NavEntry => entry !== null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, permissions, tenantConfig?.enabledIntegrations, tenantConfig?.enabledModules, isSuperAdminRoute, isSuperAdmin, superAdminNavigation]);

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Get all group names from filtered navigation
  const allGroupNames = useMemo(() => {
    return filteredNavigation
      .filter(isNavGroup)
      .map(group => group.name);
  }, [filteredNavigation]);

  const areAllCollapsed = allGroupNames.length > 0 && allGroupNames.every(name => collapsedGroups.has(name));

  const toggleCollapseAll = () => {
    if (areAllCollapsed) {
      // Expand all
      setCollapsedGroups(new Set());
    } else {
      // Collapse all
      setCollapsedGroups(new Set(allGroupNames));
    }
  };

  // Check if any item in a group is active
  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item =>
      location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    );
  };

  // Auto-expand group containing active item
  useEffect(() => {
    filteredNavigation.forEach(entry => {
      if (isNavGroup(entry) && isGroupActive(entry)) {
        setCollapsedGroups(prev => {
          const next = new Set(prev);
          next.delete(entry.name);
          return next;
        });
      }
    });
    // Only run on initial mount and route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, filteredNavigation]);


  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const renderNavItem = (item: NavItem, onClick?: () => void) => {
    const isActive = location.pathname === item.href ||
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href + '/'));
    const primaryColor = tenantConfig?.primaryColor || '#6366f1'; // Fallback to default primary

    if (item.locked) {
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onClick}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-text-muted opacity-70 hover:opacity-100 hover:bg-surface-hover transition-opacity"
          title={item.module ? `${MODULE_LABELS[item.module]} is not included in your plan` : 'Not included in your plan'}
          aria-label={`${item.name} (not included in your plan)`}
        >
          <item.icon size={20} />
          <span className="flex-1">{item.name}</span>
          <Lock size={14} aria-hidden="true" />
        </Link>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? 'text-white'
            : 'text-text-secondary hover:bg-surface-hover hover:text-text'
        }`}
        style={isActive ? { backgroundColor: primaryColor } : undefined}
      >
        <item.icon size={20} />
        <span>{item.name}</span>
      </Link>
    );
  };

  const renderNavGroup = (group: NavGroup, onClick?: () => void) => {
    const isCollapsed = collapsedGroups.has(group.name);
    const hasActiveItem = isGroupActive(group);
    const primaryColor = tenantConfig?.primaryColor || '#6366f1'; // Fallback to default primary

    // Convert hex to rgb for alpha transparency
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : { r: 99, g: 102, b: 241 }; // Fallback RGB
    };

    const rgb = hexToRgb(primaryColor);
    const bgColorWithAlpha = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;

    return (
      <div key={group.name} className="space-y-1">
        <button
          onClick={() => toggleGroup(group.name)}
          className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
            hasActiveItem && isCollapsed
              ? ''
              : 'text-text-secondary hover:bg-surface-hover hover:text-text'
          }`}
          style={
            hasActiveItem && isCollapsed
              ? { backgroundColor: bgColorWithAlpha, color: primaryColor }
              : undefined
          }
        >
          <div className="flex items-center gap-3">
            <group.icon size={20} />
            <span className="font-medium">{group.name}</span>
          </div>
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>

        {!isCollapsed && (
          <div className="ml-4 pl-3 border-l border-border space-y-1">
            {group.items.map(item => renderNavItem(item, onClick))}
          </div>
        )}
      </div>
    );
  };

  const renderNavigation = (onClick?: () => void) => (
    <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
      {/* Collapse/Expand All Button */}
      {allGroupNames.length > 0 && (
        <div className="flex justify-end pb-1">
          <button
            onClick={toggleCollapseAll}
            className="p-1.5 text-text-secondary hover:text-text hover:bg-surface-hover rounded transition-colors"
            title={areAllCollapsed ? 'Expand all' : 'Collapse all'}
          >
            {areAllCollapsed ? <ChevronsUpDown size={16} /> : <ChevronsDownUp size={16} />}
          </button>
        </div>
      )}
      {filteredNavigation.map(entry =>
        isNavGroup(entry)
          ? renderNavGroup(entry, onClick)
          : renderNavItem(entry, onClick)
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:h-screen lg:sticky lg:top-0 bg-surface border-r border-border">
        <div className="p-6">
          <div className="flex items-center gap-3">
            {tenantConfig?.logoUrl && (
              <img
                src={tenantConfig.logoUrl}
                alt={tenantConfig.companyName}
                className="h-8 w-auto"
              />
            )}
            <span className="font-bold text-text">{tenantConfig?.companyName}</span>
          </div>
        </div>

        {renderNavigation()}

        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-text-secondary truncate">{user?.email}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsQuickSearchOpen(true)}
                className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
                title="Quick Search (Shift+Shift)"
              >
                <Search size={18} className="text-text-secondary" />
              </button>
              <button
                onClick={() => setIsCommandPaletteOpen(true)}
                className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
                title="AI Assistant (Cmd+K)"
              >
                <Sparkles size={18} className="text-purple-500" />
              </button>
              <ThemeToggle />
            </div>
          </div>
          <div className="space-y-1">
            <Link
              to="/dashboard/account"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-text hover:bg-surface rounded-lg transition-colors"
            >
              <User size={16} />
              My Account
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-error hover:bg-surface rounded-lg transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed top-0 left-0 bottom-0 w-64 max-w-[85vw] bg-surface border-r border-border flex flex-col shadow-xl overflow-hidden">
            <div className="p-4 sm:p-6 flex items-center justify-between gap-2 flex-shrink-0">
              <span className="font-bold text-text truncate">{tenantConfig?.companyName}</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 -m-2 rounded-lg hover:bg-surface-secondary transition-colors"
                aria-label="Close menu"
              >
                <X size={24} className="text-text" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderNavigation(() => setSidebarOpen(false))}
            </div>
            {/* User info and logout for mobile */}
            <div className="p-4 border-t border-border flex-shrink-0 bg-surface">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-text-secondary truncate">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-1">
                <Link
                  to="/dashboard/account"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-text hover:bg-surface-hover rounded-lg transition-colors"
                >
                  <User size={16} />
                  My Account
                </Link>
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-error hover:bg-surface-hover rounded-lg transition-colors"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar - Mobile */}
        <header className="lg:hidden bg-surface border-b border-border px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu size={24} className="text-text" />
          </button>
          <span className="font-bold text-text">{tenantConfig?.companyName}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsQuickSearchOpen(true)}
              className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
              title="Quick Search"
            >
              <Search size={20} className="text-text-secondary" />
            </button>
            <button
              onClick={() => setIsCommandPaletteOpen(true)}
              className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
              title="AI Assistant"
            >
              <Sparkles size={20} className="text-purple-500" />
            </button>
            <ThemeToggle />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />

      {/* Quick Search (Shift+Shift) */}
      <QuickSearch
        isOpen={isQuickSearchOpen}
        onClose={() => setIsQuickSearchOpen(false)}
      />
    </div>
  );
}
